import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SupportedLanguage } from '@/i18n'
import type { NewsArticle, NewsCopy } from '@/data/news'
import { NewsArticleView } from './NewsArticleView'

export function NewsDialog({ article, language, onClose, returnFocus, copyOverride }: {
  article: NewsArticle | null
  language: SupportedLanguage
  onClose: () => void
  returnFocus: HTMLElement | null
  copyOverride?: NewsCopy
}) {
  const { t } = useTranslation('common')
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = ref.current
    if (!dialog || !article) return
    const overflow = document.body.style.overflow
    dialog.showModal()
    document.body.style.overflow = 'hidden'
    const focusable = () => [...dialog.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), [tabindex="0"]')].filter((element) => element.getClientRects().length)
    requestAnimationFrame(() => dialog.querySelector<HTMLElement>('.news-dialog-close')?.focus())
    const keyboard = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const nodes = focusable()
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    dialog.addEventListener('keydown', keyboard)
    return () => {
      dialog.removeEventListener('keydown', keyboard)
      dialog.close()
      document.body.style.overflow = overflow
      const target = returnFocus?.isConnected ? returnFocus : document.querySelector<HTMLElement>('.news-page h1')
      requestAnimationFrame(() => target?.focus({ preventScroll: true }))
    }
  }, [article, returnFocus])
  return <dialog ref={ref} className="news-dialog" aria-label={article ? t('news.dialogLabel') : undefined}
    onCancel={(event) => { event.preventDefault(); onClose() }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose()
    }}>
    {article && <><div className="news-dialog-toolbar"><button type="button" className="icon-button news-dialog-close" onClick={onClose} aria-label={t('news.closeArticle')}><X aria-hidden="true" /></button></div><NewsArticleView article={article} language={language} copyOverride={copyOverride} /></>}
  </dialog>
}
